import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Home, FileSearch, Receipt, MessageSquareWarning, FileText, Calculator, ArrowRight, CircleParking, Link2, BadgeCheck } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { StatusBadge } from "../../components/common";
import { CitizenPropertyCard } from "../../components/CitizenPropertyCard";   // Phase 205
import { SafStatusBadge } from "../../components/saf/SafStatus";   // Phase 207
import { api, fmtINR } from "../../lib/api";
import { useLiveCollections } from "../../lib/liveCollections";
import { useAuth } from "../../context/AuthContext";
import { useRoleModules } from "../../lib/roleModules";

export default function CitizenHome() {
  const { code } = useParams();
  const { auth } = useAuth();
  const { allowed, tenantModule } = useRoleModules(code);
  const [props, setProps] = useState(null);   // null = loading
  const [grievances, setGrievances] = useState([]);
  const [applications, setApplications] = useState([]);
  const [safs, setSafs] = useState(null);   // Phase 207: my self-assessments (latest shown on the dashboard)

  const live = useLiveCollections(code);   // Phase 149: total due follows the live ledger
  useEffect(() => {
    api.get(`/t/${code}/citizen/my-properties`).then((r) => setProps(r.data)).catch(() => setProps((p) => p || []));
  }, [code, live]);
  useEffect(() => {
    api.get(`/t/${code}/grievances`).then((r) => setGrievances(r.data)).catch(() => {});
    api.get(`/t/${code}/applications`).then((r) => setApplications(r.data)).catch(() => {});
    api.get(`/t/${code}/saf/mine`).then((r) => setSafs(r.data)).catch(() => setSafs([]));
  }, [code]);

  const totalDue = (props || []).reduce((s, p) => s + (p.demand_summary?.balance || 0), 0);
  const latestSaf = (safs || [])[0] || null;
  const openSafs = (safs || []).filter((s) => !["approved", "rejected"].includes(s.status)).length;

  const tiles = [
    { icon: FileSearch, label: "My Properties", to: `/${code}/portal/properties`, testId: "tile-search", module: "properties" },
    { icon: Calculator, label: "Self Assessment", to: `/${code}/portal/self-assessment`, testId: "tile-self-assessment", module: "self_assessment" },
    { icon: FileText, label: "Apply for Services", to: `/${code}/portal/services`, testId: "tile-services", module: "services" },
    { icon: MessageSquareWarning, label: "Raise Grievance", to: `/${code}/portal/grievances`, testId: "tile-grievance", module: "grievances" },
    // Phase 201: only where the Super Admin assigned Smart Parking to this municipality
    ...(tenantModule("smart_parking") ? [{ icon: CircleParking, label: "Smart Parking", to: `/${code}/portal/parking`, testId: "tile-parking", module: "parking" }] : []),
  ].filter((t) => allowed(t.module));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold font-heading">Welcome, {auth?.user?.name?.split(" ")[0]}</h1>
        <p className="text-muted-foreground text-sm">Your municipal services dashboard</p>
      </div>

      {totalDue > 0 && (
        <Card className="bg-amber-50 border-amber-200 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="font-medium text-amber-900">You have pending tax dues</p>
              <p className="text-2xl font-semibold text-amber-900 tabular-nums">{fmtINR(totalDue)}</p>
            </div>
            <Button asChild data-testid="pay-dues-cta">
              <Link to={`/${code}/portal/properties`}>View & Pay <ArrowRight className="h-4 w-4 ml-2" /></Link>
            </Button>
          </CardContent>
        </Card>
      )}

      <div className={`grid grid-cols-2 gap-3 ${tiles.length >= 5 ? "lg:grid-cols-5" : "lg:grid-cols-4"}`}>
        {tiles.map((t) => (
          <Link key={t.label} to={t.to} data-testid={t.testId}>
            <Card className="shadow-sm hover:shadow-md transition-shadow h-full">
              <CardContent className="p-4 flex flex-col items-center text-center gap-2 py-6">
                <div className="h-11 w-11 rounded-xl bg-accent flex items-center justify-center">
                  <t.icon className="h-5 w-5 text-accent-foreground" />
                </div>
                <p className="text-sm font-medium">{t.label}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Phase 207: latest self-assessment status — "Assessment Approved — Your PIN is …" once the admin approves */}
      {allowed("self_assessment") && latestSaf && (
        <Card className={`shadow-sm ${latestSaf.status === "approved" ? "border-emerald-300 bg-emerald-50/40" : latestSaf.status === "rejected" ? "border-red-200" : "border-primary/30"}`}
          data-testid="citizen-home-saf-card" data-status={latestSaf.status}>
          <CardContent className="p-4 flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${latestSaf.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-accent text-accent-foreground"}`}>
                {latestSaf.status === "approved" ? <BadgeCheck className="h-5 w-5" /> : <Calculator className="h-5 w-5" />}
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Self-assessment <span className="font-mono">{latestSaf.saf_id}</span>{openSafs > 1 ? ` · ${openSafs} in progress` : ""}</p>
                {latestSaf.status === "approved" ? (
                  <p className="font-semibold text-emerald-900" data-testid="citizen-home-saf-approved">Assessment Approved — Your PIN is <span className="font-mono" data-testid="citizen-home-saf-pin">{latestSaf.pin}</span></p>
                ) : (
                  <p className="font-medium flex items-center gap-2 flex-wrap" data-testid="citizen-home-saf-status">{latestSaf.owner_name} <SafStatusBadge status={latestSaf.status} /></p>
                )}
                <p className="text-xs text-muted-foreground mt-0.5">
                  {latestSaf.status === "approved" && "The property is linked to your account — view the demand and pay online."}
                  {latestSaf.status === "pending_verification" && (latestSaf.assigned_collector_name ? `Field Collector ${latestSaf.assigned_collector_name} will visit for physical verification.` : "Waiting for a Field Collector to be assigned.")}
                  {latestSaf.status === "sent_back" && "Under correction by the Field Collector."}
                  {latestSaf.status === "collector_verified" && "Verified on site — awaiting Supervisor review."}
                  {latestSaf.status === "supervisor_approved" && "Awaiting the Municipal Admin's final approval."}
                  {latestSaf.status === "rejected" && `Rejected: ${latestSaf.rejection_remarks || "see details"}`}
                </p>
              </div>
            </div>
            <Button asChild size="sm" variant={latestSaf.status === "approved" ? "default" : "outline"} data-testid="citizen-home-saf-link">
              <Link to={latestSaf.status === "approved" ? `/${code}/portal/properties` : `/${code}/portal/self-assessment?tab=mine`}>
                {latestSaf.status === "approved" ? "View & pay" : "Track progress"} <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Phase 205: every linked property in one view — dues, last payment and who to call */}
      {allowed("properties") && (
        <section className="space-y-3" data-testid="citizen-home-properties">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="font-medium flex items-center gap-2"><Home className="h-4 w-4" /> My properties{props ? <span className="text-xs text-muted-foreground font-normal">({props.length})</span> : null}</h2>
              <p className="text-xs text-muted-foreground">Dues, last payment and your collector's contact for every property linked to your account.</p>
            </div>
            <div className="flex items-center gap-2">
              {props && props.length > 0 && (
                <Button asChild variant="ghost" size="sm"><Link to={`/${code}/portal/properties`} data-testid="citizen-home-view-all">View details</Link></Button>
              )}
              <Button asChild size="sm" variant={props && props.length ? "outline" : "default"}>
                <Link to={`/${code}/portal/link-properties`} data-testid="citizen-home-link-property"><Link2 className="h-3.5 w-3.5 mr-1.5" /> Link a property</Link>
              </Button>
            </div>
          </div>
          {props === null ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="citizen-home-properties-skeleton">
              {[0, 1].map((i) => <Skeleton key={i} className="h-56 rounded-xl" />)}
            </div>
          ) : props.length === 0 ? (
            <Card className="shadow-sm border-dashed" data-testid="citizen-home-no-properties">
              <CardContent className="p-6 text-center space-y-2">
                <div className="h-11 w-11 rounded-xl bg-accent mx-auto flex items-center justify-center"><Link2 className="h-5 w-5 text-accent-foreground" /></div>
                <p className="font-medium">No properties linked yet</p>
                <p className="text-sm text-muted-foreground">Link your property with its PIN to see dues, pay online and find your collector's number.</p>
                <Button asChild><Link to={`/${code}/portal/link-properties`} data-testid="citizen-home-link-first-property">Link your first property <ArrowRight className="h-4 w-4 ml-2" /></Link></Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="citizen-home-properties-grid">
              {props.map((p) => <CitizenPropertyCard key={p.id} code={code} prop={p} onUnlinked={(x) => setProps((xs) => (xs || []).filter((q) => q.id !== x.id))} />)}
            </div>
          )}
        </section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <p className="font-medium mb-3 flex items-center gap-2"><Receipt className="h-4 w-4" /> Recent activity</p>
            {[...grievances.slice(0, 2).map((g) => ({ id: g.id, label: g.ticket_number, sub: g.subject, status: g.status })),
              ...applications.slice(0, 2).map((a) => ({ id: a.id, label: a.application_number, sub: a.service_name, status: a.status }))].length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No grievances or applications yet</p>
            ) : (
              <>
                {grievances.slice(0, 2).map((g) => (
                  <div key={g.id} className="flex items-center justify-between border rounded-lg px-3 py-2 mb-2">
                    <div><p className="font-mono text-sm">{g.ticket_number}</p><p className="text-xs text-muted-foreground">{g.subject}</p></div>
                    <StatusBadge status={g.status} />
                  </div>
                ))}
                {applications.slice(0, 2).map((a) => (
                  <div key={a.id} className="flex items-center justify-between border rounded-lg px-3 py-2 mb-2">
                    <div><p className="font-mono text-sm">{a.application_number}</p><p className="text-xs text-muted-foreground">{a.service_name}</p></div>
                    <StatusBadge status={a.status} />
                  </div>
                ))}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
